/**
 * Pricing Page - Landing page showing all 4 subscription plans
 */

import { useState, useEffect } from "react";
import { Check, Sparkles, Building, Users, Zap } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import SignUpWithPlanForm from "@/global/components/forms/SignUpWithPlanForm";
import LoginModal from "@/components/forms/LoginModal";
import AgentSignUpForm from "@/global/components/forms/AgentSignUpForm";
import { motion, useScroll, useTransform } from "framer-motion";
import { OrcaBackground } from "@/global/components/premium-ui/backgrounds";
import { TextReveal } from "@/global/components/premium-ui/animations/TextReveal";
import { MagneticCard } from "@/global/components/premium-ui/animations/MagneticCard";

interface Plan {
  code: string;
  name: string;
  tagline: string;
  price: string;
  yearlyPrice?: string;
  icon: React.ReactNode;
  popular?: boolean;
  features: string[];
  cta: string;
  badge?: string;
}

const plans: Plan[] = [
  {
    code: "fma_basic",
    name: "FMA Basic",
    tagline: "Perfect for getting started",
    price: "Free",
    icon: <Users className="h-6 w-6" />,
    features: [
      "Basic agency profile",
      "Watch Training Videos",
      "Connect with agents",
      "Free listing of your agency",
      "Community access",
    ],
    cta: "Get Started Free",
  },
  {
    code: "fma_pro",
    name: "FMA Pro",
    tagline: "For growing agencies",
    price: "$399",
    yearlyPrice: "$3,600",
    icon: <Building className="h-6 w-6" />,
    features: [
      "Everything in Basic",
      "Access to post Training Videos",
      "Priority agent matching",
      "Robust Profile Page",
      "Featured agency listing",
      "Higher Search Ranking",
    ],
    cta: "Start Pro Trial",
  },
  {
    code: "fma_elite",
    name: "FMA Elite",
    tagline: "Maximum growth & control",
    price: "$899",
    yearlyPrice: "$8,500",
    icon: <Sparkles className="h-6 w-6" />,
    popular: true,
    badge: "Includes AMS",
    features: [
      "Everything in Pro",
      "Full AMS-CRM access included",
      "Premium placement in search",
      "Dedicated account manager",
      "Custom Calendar Bookings",
      "Analytics & reporting",
      "Access to Earn Points & Ranking",
    ],
    cta: "Go Elite",
  },
  {
    code: "ams_standard",
    name: "Agency Management Software",
    tagline: "Powerful agency management",
    price: "$59",
    yearlyPrice: "$500",
    icon: <Zap className="h-6 w-6" />,
    features: [
      "Full CRM system",
      "Client management",
      "Agent tracking",
      "Pipeline automation",
      "Email integration",
      "Analytics & reporting",
    ],
    cta: "Add AMS",
  },
];

export default function PricingPage() {
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [billingInterval, setBillingInterval] = useState<"monthly" | "annual">(
    "monthly"
  );
  const [showSignup, setShowSignup] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showAgentSignup, setShowAgentSignup] = useState(false);

  const handleSelectPlan = (planCode: string) => {
    setSelectedPlan(planCode);
    setShowSignup(true);
  };

  const handleCloseSignup = () => {
    setShowSignup(false);
    setSelectedPlan(null);
  };

  const handleOpenLogin = () => {
    setShowLogin(true);
  };

  const handleCloseLogin = () => {
    setShowLogin(false);
  };

  return (
    <div className="min-h-screen bg-[#0A1628] relative overflow-hidden">
      {/* Unified Background System - No Blur Effects */}
      <OrcaBackground
        quality="auto"
        layers={{
          sparkles: true,
          particles: true,
          grid: true,
          vignette: true,
        }}
      />

      {/* Hero Section - Starts immediately without redundant header */}
      <div className="relative z-10 container mx-auto px-4 sm:px-6 py-12 sm:py-16 lg:py-20 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <motion.h2
            className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-light text-white mb-6 sm:mb-8 tracking-tight"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            Choose Your{" "}
            <span className="bg-linear-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent font-medium">
              Perfect Plan
            </span>
          </motion.h2>
          <TextReveal
            text="Connect agents with agencies, manage your business, and grow your team with the right tools for your needs."
            className="text-lg sm:text-xl text-gray-300 font-light max-w-3xl mx-auto leading-relaxed"
            delay={0.6}
          />
        </motion.div>
      </div>

      {/* Agent Signup Section */}
      <div className="relative z-10 container mx-auto px-4 sm:px-6 pb-8 sm:pb-12 lg:pb-16">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="relative overflow-hidden rounded-2xl sm:rounded-3xl border border-white/10 shadow-2xl"
        >
          {/* Background effects for agent section */}
          <div className="absolute inset-0 bg-white/5 backdrop-blur-md rounded-3xl"></div>

          <div className="relative p-6 sm:p-8 lg:p-12">
            <div className="text-center space-y-6 sm:space-y-8">
              <motion.div
                className="relative inline-block"
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{
                  type: "spring" as const,
                  damping: 15,
                  delay: 0.2,
                }}
              >
                <div className="inline-flex items-center justify-center h-20 w-20 bg-purple-400/20 text-purple-400 rounded-full mb-6 shadow-lg shadow-purple-500/25">
                  <Users className="h-10 w-10" />
                </div>
                <div className="absolute inset-0 bg-purple-400 rounded-full blur-xl opacity-30"></div>
              </motion.div>

              <div>
                <motion.h3
                  className="text-4xl md:text-5xl font-light text-white mb-6 tracking-tight"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3, duration: 0.6 }}
                >
                  Are you an{" "}
                  <span className="bg-linear-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent font-medium">
                    Agent?
                  </span>
                </motion.h3>
                <TextReveal
                  text="Join for free and start browsing agencies, connect with potential partners, and grow your insurance career with the perfect match."
                  className="text-xl text-gray-300 font-light max-w-3xl mx-auto leading-relaxed"
                  delay={0.5}
                />
              </div>

              <motion.div
                className="flex flex-col sm:flex-row gap-6 justify-center items-center pt-4"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.7, duration: 0.6 }}
              >
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button
                    size="lg"
                    className="px-10 py-4 text-lg bg-linear-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white border-0 shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 transition-all duration-300"
                    onClick={() => setShowAgentSignup(true)}
                  >
                    <Users className="mr-3 h-6 w-6" />
                    Sign Up as Agent - Free
                  </Button>
                </motion.div>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button
                    variant="outline"
                    size="lg"
                    className="px-10 py-4 text-lg text-white border-white/20 bg-white/5 backdrop-blur-sm hover:bg-white/10 hover:border-white/30 transition-all duration-300"
                    onClick={handleOpenLogin}
                  >
                    Have an account? Log In
                  </Button>
                </motion.div>
              </motion.div>

              <motion.div
                className="flex flex-wrap items-center justify-center gap-4 sm:gap-8 pt-6 text-xs sm:text-sm text-gray-400 font-light"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.9, duration: 0.6 }}
              >
                <span className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                  Agents: Free forever
                </span>
                <span className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                  Browse agencies
                </span>
                <span className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                  Connect with the community
                </span>
                <span className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                  Build your profile
                </span>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Billing Toggle - Moved below agent signup */}
      <motion.div
        className="relative z-10 container mx-auto px-4 text-center mb-16"
        initial={{ opacity: 0, scale: 0.9 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <div className="inline-flex items-center bg-white/5 backdrop-blur-md rounded-2xl p-2 border border-white/10 shadow-lg">
          <motion.button
            onClick={() => setBillingInterval("monthly")}
            className={`px-6 py-3 rounded-xl transition-all duration-300 text-lg font-medium ${
              billingInterval === "monthly"
                ? "bg-orange-500 text-white shadow-lg shadow-orange-500/25"
                : "text-gray-300 hover:text-white hover:bg-white/5"
            }`}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Monthly
          </motion.button>
          <motion.button
            onClick={() => setBillingInterval("annual")}
            className={`px-6 py-3 rounded-xl transition-all duration-300 text-lg font-medium ${
              billingInterval === "annual"
                ? "bg-linear-to-r from-purple-500 to-orange-500 text-white shadow-lg shadow-purple-500/25"
                : "text-gray-300 hover:text-white hover:bg-white/5"
            }`}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Annual{" "}
            <span className="text-sm text-purple-300 ml-2">(Save 17%)</span>
          </motion.button>
        </div>
      </motion.div>

      {/* Pricing Cards */}
      <div className="relative z-10 container mx-auto px-4 pb-24">
        <motion.div
          id="plans"
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h3 className="text-4xl md:text-5xl font-light text-white mb-6 tracking-tight">
            Agency{" "}
            <span className="bg-linear-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent font-medium">
              Plans
            </span>
          </h3>
          <TextReveal
            text="Comprehensive plans for insurance agencies to recruit agents and manage their business with cutting-edge tools."
            className="text-xl text-gray-300 font-light max-w-3xl mx-auto leading-relaxed"
            delay={0.3}
          />
        </motion.div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={{
            hidden: {},
            visible: {
              transition: {
                staggerChildren: 0.15,
              },
            },
          }}
        >
          {plans.map((plan, index) => (
            <motion.div
              key={plan.code}
              variants={{
                hidden: { opacity: 0, y: 50, scale: 0.9 },
                visible: {
                  opacity: 1,
                  y: 0,
                  scale: 1,
                  transition: {
                    type: "spring" as const,
                    damping: 20,
                    stiffness: 100,
                  },
                },
              }}
              className={`group relative flex flex-col ${
                plan.popular ? "lg:scale-110" : ""
              }`}
            >
              <MagneticCard strength={0.05}>
                {/* Card background with glassmorphism */}
                <div className="absolute inset-0 bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 shadow-2xl"></div>

                {/* Popular badge */}
                {plan.popular && (
                  <motion.div
                    className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10"
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{
                      type: "spring" as const,
                      damping: 15,
                      delay: 0.5,
                    }}
                  >
                    <div className="bg-linear-to-r from-orange-500 to-orange-600 text-white px-6 py-2 rounded-full text-sm font-medium shadow-lg shadow-orange-500/25">
                      Most Value
                    </div>
                  </motion.div>
                )}

                {/* Hover glow effect */}
                <motion.div
                  className={`absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-500 ${
                    plan.popular
                      ? "shadow-2xl shadow-orange-500/20"
                      : "shadow-xl shadow-blue-500/10"
                  }`}
                  whileHover={{ opacity: 1 }}
                ></motion.div>

                <div className="relative p-8 flex flex-col h-full">
                  {/* Header */}
                  <div className="mb-8">
                    <div className="flex items-center justify-between mb-6">
                      <motion.div
                        className={`relative p-3 rounded-xl shadow-lg ${
                          plan.popular
                            ? "bg-orange-500/20 text-orange-400 shadow-orange-500/25"
                            : "bg-blue-500/20 text-blue-400 shadow-blue-500/25"
                        }`}
                        whileHover={{
                          rotate: [0, -10, 10, -10, 0],
                          scale: 1.1,
                        }}
                        transition={{ duration: 0.5 }}
                      >
                        {plan.icon}
                        <div
                          className={`absolute inset-0 rounded-xl blur-lg opacity-30 ${
                            plan.popular ? "bg-orange-400" : "bg-blue-400"
                          }`}
                        ></div>
                      </motion.div>
                      {plan.badge && (
                        <div className="bg-white/10 text-gray-300 px-3 py-1 rounded-full text-xs font-medium border border-white/20">
                          {plan.badge}
                        </div>
                      )}
                    </div>

                    <h4 className="text-2xl font-semibold text-white mb-2 tracking-tight">
                      {plan.name}
                    </h4>
                    <p className="text-gray-400 text-sm font-light">
                      {plan.tagline}
                    </p>
                  </div>

                  {/* Pricing */}
                  <div className="mb-8">
                    <div className="flex items-baseline mb-2">
                      <span className="text-4xl font-light text-white">
                        {plan.price}
                      </span>
                      {plan.price !== "Free" && (
                        <span className="text-gray-400 ml-2 font-light">
                          /month
                        </span>
                      )}
                    </div>
                    {plan.yearlyPrice && billingInterval === "annual" && (
                      <p className="text-sm text-gray-400 font-light">
                        or {plan.yearlyPrice}/year
                      </p>
                    )}
                  </div>

                  {/* Features */}
                  <div className="grow mb-8">
                    <ul className="space-y-4">
                      {plan.features.map((feature, featureIndex) => (
                        <motion.li
                          key={featureIndex}
                          className="flex items-start"
                          initial={{ opacity: 0, x: -10 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: featureIndex * 0.05 }}
                        >
                          <Check
                            className={`h-5 w-5 mr-3 shrink-0 mt-0.5 ${
                              plan.popular ? "text-orange-400" : "text-blue-400"
                            }`}
                          />
                          <span className="text-sm text-gray-300 font-light leading-relaxed">
                            {feature}
                          </span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>

                  {/* CTA Button */}
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Button
                      onClick={() => handleSelectPlan(plan.code)}
                      className={`w-full py-3 text-base font-medium transition-all duration-300 ${
                        plan.popular
                          ? "bg-linear-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40"
                          : "bg-white/5 text-white border border-white/20 hover:bg-white/10 hover:border-white/30 backdrop-blur-sm"
                      }`}
                      size="lg"
                    >
                      {plan.cta}
                    </Button>
                  </motion.div>
                </div>
              </MagneticCard>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Features Section */}
      <div className="relative z-10 container mx-auto px-4 py-24">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="relative overflow-hidden rounded-3xl border border-white/10 shadow-2xl"
        >
          {/* Background effects */}
          <div className="absolute inset-0 bg-white/5 backdrop-blur-md rounded-3xl"></div>

          <div className="relative py-20 px-12">
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <h3 className="text-4xl md:text-5xl font-light text-white mb-6 tracking-tight">
                Why Choose{" "}
                <span className="bg-linear-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent font-medium">
                  Find My Agency?
                </span>
              </h3>
              <TextReveal
                text="The only platform designed specifically for insurance agencies and agents to connect, grow, and succeed together."
                className="text-xl text-gray-300 font-light max-w-3xl mx-auto leading-relaxed"
                delay={0.4}
              />
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-12 max-w-6xl mx-auto">
              {[
                {
                  icon: Users,
                  title: "Targeted Matching",
                  description:
                    "Connect with the right agents or agencies based on experience, location, and goals with our intelligent matching system.",
                  color: "orange",
                  delay: 0,
                },
                {
                  icon: Zap,
                  title: "Powerful Tools",
                  description:
                    "Manage your entire agency with our integrated CRM and automation features designed for the modern insurance business.",
                  color: "blue",
                  delay: 0.2,
                },
                {
                  icon: Sparkles,
                  title: "Proven Results",
                  description:
                    "Join hundreds of agencies and agents who have found their perfect match and accelerated their growth with our platform.",
                  color: "purple",
                  delay: 0.4,
                },
              ].map((feature, idx) => (
                <motion.div
                  key={feature.title}
                  className="text-center group"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.6 + feature.delay, duration: 0.6 }}
                  whileHover={{ scale: 1.05 }}
                >
                  <motion.div
                    className="relative mb-8"
                    whileHover={{ rotate: [0, -10, 10, 0] }}
                    transition={{ duration: 0.5 }}
                  >
                    <div
                      className={`inline-flex items-center justify-center h-16 w-16 bg-${feature.color}-500/20 text-${feature.color}-400 rounded-2xl mb-6 shadow-lg shadow-${feature.color}-500/25`}
                    >
                      <feature.icon className="h-8 w-8" />
                    </div>
                    <div
                      className={`absolute inset-0 bg-${feature.color}-400 rounded-2xl blur-xl opacity-20 group-hover:opacity-40 transition-opacity duration-500`}
                    ></div>
                  </motion.div>
                  <h4 className="text-xl font-semibold text-white mb-4 tracking-tight">
                    {feature.title}
                  </h4>
                  <p className="text-gray-300 font-light leading-relaxed">
                    {feature.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Footer */}
      <div className="relative z-10 container mx-auto px-4 py-12">
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 p-8 max-w-2xl mx-auto">
            <p className="text-gray-300 font-light mb-4">
              Questions? Contact us at{" "}
              <motion.a
                href="mailto:support@findmyagency.us"
                className="text-orange-400 hover:text-orange-300 transition-colors duration-300 hover:underline"
                whileHover={{ scale: 1.05 }}
              >
                support@findmyagency.us
              </motion.a>
            </p>
            <p className="text-gray-400 text-sm font-light">
              © 2025 Find My Agency. All rights reserved.
            </p>
          </div>
        </motion.div>
      </div>

      {/* Signup Modal */}
      {showSignup && selectedPlan && (
        <SignUpWithPlanForm
          planCode={selectedPlan}
          billingInterval={billingInterval}
          onClose={handleCloseSignup}
        />
      )}

      {/* Login Modal */}
      <LoginModal isOpen={showLogin} onClose={handleCloseLogin} />

      {/* Agent Signup Modal */}
      {showAgentSignup && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center p-4 z-9999 overflow-y-auto"
          onClick={() => setShowAgentSignup(false)}
        >
          <div
            className="w-full max-w-md bg-white/10 backdrop-blur-xl border border-white/20 shadow-2xl rounded-2xl my-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <AgentSignUpForm
              onClose={() => setShowAgentSignup(false)}
              onSwitchToLogin={() => {
                setShowAgentSignup(false);
                setShowLogin(true);
              }}
              redirectAfterSignup={false}
            />
          </div>
        </div>
      )}
    </div>
  );
}
